"use client";

import { Menu, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { useState } from "react";

import { Button } from "@/components/Button";
import { ButtonLink } from "@/components/ButtonLink";
import { MarketingLogo } from "@/components/MarketingLogo";
import { primaryNav } from "@/content/nav";
import { getFrontendLoginUrl, getFrontendSignupUrl } from "@/lib/urls";

import { LanguageSwitcher } from "../LanguageSwitcher";
import {
  navActionButtonClass,
  navBarRowClass,
  navCenterLinksClass,
  navGlassShellClass,
  navHeaderClass,
  navPillLinkClass,
} from "./Nav.constants";
import { MobileNavFallback } from "./MobileNavFallback";
import { NavFeaturesMenu } from "./NavFeaturesMenu";
import { useMobileNav } from "./useMobileNav";

export const Nav = () => {
  const t = useTranslations();
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileNav = useMobileNav();
  const { MobileNav } = mobileNav;

  /** Warm the menu chunk on pointer or focus, before the tap lands. */
  const prefetchMobileNav = () => {
    if (!MobileNav) mobileNav.request(false);
  };

  return (
    <header className={navHeaderClass}>
      <div className="marketing-grid">
        <div className={navGlassShellClass}>
          <div className={navBarRowClass}>
            <MarketingLogo priority />

            <nav
              className={navCenterLinksClass}
              aria-label={t("nav.a11y.primaryNav")}
            >
              <NavFeaturesMenu />
              {primaryNav.map((item) => {
                const isActive =
                  pathname === item.href ||
                  pathname.startsWith(`${item.href}/`);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={navPillLinkClass(isActive)}
                  >
                    {t(item.labelKey)}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center justify-end justify-self-end gap-1.5 sm:gap-2">
              <div className="hidden items-center gap-1.5 lg:flex">
                <LanguageSwitcher variant="compact" />
                <Button
                  variant="ghost"
                  size="sm"
                  className={navActionButtonClass}
                  render={<a href={getFrontendLoginUrl()} />}
                >
                  {t("nav.login")}
                </Button>
                <ButtonLink
                  variant="brand"
                  size="sm"
                  href={getFrontendSignupUrl()}
                >
                  {t("nav.breakDownScript")}
                </ButtonLink>
              </div>

              {/* Below lg the actions above are hidden, so sign-up stays one tap away next to the menu. */}
              <ButtonLink
                variant="brand"
                size="sm"
                href={getFrontendSignupUrl()}
                className="lg:hidden"
                trackingLocation="nav_mobile_signup"
              >
                {t("nav.breakDownScript")}
              </ButtonLink>

              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label={
                  mobileOpen ? t("nav.a11y.closeMenu") : t("nav.a11y.openMenu")
                }
                onPointerEnter={prefetchMobileNav}
                onPointerDown={prefetchMobileNav}
                onFocus={prefetchMobileNav}
                onClick={() => {
                  if (!MobileNav && !mobileOpen) mobileNav.request(true);
                  setMobileOpen((open) => !open);
                }}
              >
                {mobileOpen ? (
                  <X className="size-5" />
                ) : (
                  <Menu className="size-5" />
                )}
              </Button>
            </div>
          </div>

          {MobileNav ? (
            <MobileNav open={mobileOpen} onOpenChange={setMobileOpen} />
          ) : mobileOpen && mobileNav.failed ? (
            <MobileNavFallback onNavigate={() => setMobileOpen(false)} />
          ) : null}
        </div>
      </div>
    </header>
  );
};
